import { SITE_URL } from '@/lib/site-url';

/**
 * The app page sections that can be embedded on another site, one entry each. The
 * section's Embed button, its share dialog and the snippet's iframe height all read
 * this, so a new app section embed is an entry here plus its /embed/<path> page.
 */
export type AppEmbedType = 'ads' | 'ratings' | 'price-history';

export type EmbedTheme = 'system' | 'dark' | 'light';

type AppEmbedSpec = {
  /** Dialog title, e.g. "Embed Ads". */
  label: string;
  /** Terminal-mode dialog title. */
  terminalLabel: string;
  /** iframe height in px: the section at its default state, plus the embed header. A section can pass its own. */
  height: number;
  /** Whether the section reads a storefront, so the snippet carries ?country=. */
  perCountry: boolean;
  description: string;
  terminalDescription: string;
};

export const APP_EMBEDS: Record<AppEmbedType, AppEmbedSpec> = {
  ads: {
    label: 'Embed Ads',
    terminalLabel: 'EMBED.ADS',
    height: 520,
    perCountry: false,
    description: "This will embed the ads this app's publisher runs across the Meta, Google and Apple ad libraries.",
    terminalDescription: '// Embeds ads: Meta + Google + Apple ad libraries',
  },
  ratings: {
    label: 'Embed Ratings',
    terminalLabel: 'EMBED.RATINGS',
    height: 580,
    perCountry: true,
    description: 'This will embed an interactive chart of the rating count and average rating over time.',
    terminalDescription: '// Embeds ratings: count + average over time',
  },
  'price-history': {
    label: 'Embed Price History',
    terminalLabel: 'EMBED.PRICE',
    height: 400,
    perCountry: true,
    description: 'This will embed every price this app has charged in this storefront, and the changes between them.',
    terminalDescription: '// Embeds price history: every price + change',
  },
};

export function appEmbedUrl(type: AppEmbedType, appId: number, opts: { country?: string; theme: EmbedTheme }): string {
  const params = new URLSearchParams();
  if (APP_EMBEDS[type].perCountry) params.set('country', (opts.country || 'us').toLowerCase());
  params.set('theme', opts.theme);
  return `${SITE_URL}/embed/${type}/${appId}?${params.toString()}`;
}

export function appEmbedCode(type: AppEmbedType, appId: number, opts: { country?: string; theme: EmbedTheme; height?: number }): string {
  return `<iframe
  src="${appEmbedUrl(type, appId, opts)}"
  width="100%"
  height="${opts.height ?? APP_EMBEDS[type].height}"
  class="appstoretracker-embed appstoretracker-${type}"
  style="border: none; border-radius: 12px;"
></iframe>`;
}
